import React from 'react';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { TimelineChartProps, TimelineDeployment } from './types.js';
import { getDeploymentStatusColor } from '../utils/index.js';

/**
 * TimelineChart - Horizontal timeline visualization for BRUTAL deployments
 *
 * Renders deployments as horizontal bars on a timeline, grouped by environment.
 * Uses EITELKEIT tokens for all colors (no hard-coded values).
 *
 * @example
 * ```tsx
 * <TimelineChart
 *   deployments={[
 *     {
 *       id: '1',
 *       artifact: 'api',
 *       version: '1.2.3',
 *       environment: 'prod',
 *       status: 'deployed',
 *       startedAt: Date.now() - 3600000,
 *       completedAt: Date.now() - 1800000
 *     }
 *   ]}
 *   range={{ start: Date.now() - 7200000, end: Date.now() }}
 *   onDeploymentClick={(id) => console.log('Clicked:', id)}
 *   width={800}
 *   height={400}
 * />
 * ```
 */
export function TimelineChart({
  deployments,
  range,
  onDeploymentClick,
  width,
  height,
}: TimelineChartProps): React.JSX.Element {
  const padding = { top: 40, right: 20, bottom: 40, left: 120 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Group deployments by environment
  const environmentGroups = new Map<string, TimelineDeployment[]>();
  for (const deployment of deployments) {
    const existing = environmentGroups.get(deployment.environment);
    if (existing) {
      existing.push(deployment);
    } else {
      environmentGroups.set(deployment.environment, [deployment]);
    }
  }

  const environments = Array.from(environmentGroups.keys()).sort();
  const envHeight = chartHeight / environments.length;

  // Time scale function: maps timestamp to X coordinate
  const timeToX = (timestamp: number): number => {
    const normalized = (timestamp - range.start) / (range.end - range.start);
    return padding.left + normalized * chartWidth;
  };

  // Format time as "HH:MM"
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Generate 5 time labels along X-axis (0%, 25%, 50%, 75%, 100%)
  const timeLabels = [0, 0.25, 0.5, 0.75, 1].map((percent) => {
    const timestamp = range.start + (range.end - range.start) * percent;
    return {
      x: padding.left + percent * chartWidth,
      label: formatTime(timestamp),
    };
  });

  return (
    <svg
      width={width}
      height={height}
      style={{
        backgroundColor: tokens.colors.void,
        fontFamily: tokens.fonts.mono,
        userSelect: 'none',
      }}
    >
      {/* X-axis line */}
      <line
        x1={padding.left}
        y1={height - padding.bottom}
        x2={width - padding.right}
        y2={height - padding.bottom}
        stroke={tokens.colors.border}
        strokeWidth={1}
      />

      {/* X-axis time labels */}
      {timeLabels.map((label, i) => (
        <text
          key={i}
          x={label.x}
          y={height - padding.bottom + 20}
          textAnchor="middle"
          fill={tokens.colors.textSecondary}
          fontSize={tokens.fontSizes.xs}
        >
          {label.label}
        </text>
      ))}

      {/* Y-axis (environment labels and deployment bars) */}
      {environments.map((env, envIndex) => {
        const envY = padding.top + envIndex * envHeight;
        const envCenterY = envY + envHeight / 2;
        const envDeployments = environmentGroups.get(env) ?? [];

        return (
          <g key={env}>
            {/* Environment label */}
            <text
              x={padding.left - 10}
              y={envCenterY}
              textAnchor="end"
              dominantBaseline="middle"
              fill={tokens.colors.text}
              fontSize={tokens.fontSizes.sm}
              fontWeight={tokens.fontWeights.semibold}
            >
              {env}
            </text>

            {/* Environment separator line */}
            {envIndex > 0 && (
              <line
                x1={padding.left}
                y1={envY}
                x2={width - padding.right}
                y2={envY}
                stroke={tokens.colors.border}
                strokeWidth={1}
                opacity={0.3}
              />
            )}

            {/* Deployment bars */}
            {envDeployments.map((deployment) => {
              const startX = timeToX(deployment.startedAt);
              const endX = deployment.completedAt
                ? timeToX(deployment.completedAt)
                : timeToX(Math.min(Date.now(), range.end));
              const barWidth = Math.max(endX - startX, 2); // Minimum 2px width
              const barHeight = 20;
              const barY = envCenterY - barHeight / 2;

              const statusColor = getDeploymentStatusColor(deployment.status);

              const handleClick = () => {
                if (onDeploymentClick) {
                  onDeploymentClick(deployment.id);
                }
              };

              return (
                <g
                  key={deployment.id}
                  onClick={handleClick}
                  style={{
                    cursor: onDeploymentClick ? 'pointer' : 'default',
                  }}
                >
                  {/* Deployment bar */}
                  <rect
                    x={startX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={statusColor}
                    opacity={0.8}
                    rx={2}
                  />

                  {/* Artifact label (if bar is wide enough) */}
                  {barWidth > 50 && (
                    <text
                      x={startX + barWidth / 2}
                      y={barY + barHeight / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={tokens.colors.void}
                      fontSize={tokens.fontSizes.xs}
                      fontWeight={tokens.fontWeights.medium}
                    >
                      {deployment.artifact}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
